import { ConferenceType } from '@wui/im'
import { AsyncComponentLoader, Component, defineAsyncComponent, h, mergeProps, ref, Ref, ShallowRef, shallowRef, watch, watchEffect } from 'vue'

import { NavState } from '@/shared/types/app'

/** Не участвуют в родительском Suspense — иначе при смене вкладки briefly/навсегда видны оба панели. */
const defineSidebarAsync = (loader: AsyncComponentLoader) => defineAsyncComponent({
  loader,
  suspensible: false,
})

const ViewChatList = defineSidebarAsync(() => import('@/components/AppAsideLeft/Chat/ChatList/ChatList.vue'))
const CalendarSidebar = defineSidebarAsync(() => import('@/components/AppAsideLeft/Calendar/app-aside-calendar.vue'))
const CallsWrapper = defineSidebarAsync(() => import('@/components/AppAsideLeft/Calls/CallsWrapper/CallsWrapper.vue'))
const ViewContactList = defineSidebarAsync(() => import('@/components/AppAsideLeft/Contacts/ContactsOrGroupsWrapper/ContactsOrGroupsWrapper.vue'))
const CreateChat = defineSidebarAsync(() => import('@/components/organisms/CreateChat/CreateChat.vue'))
const SendFax = defineSidebarAsync(() => import('@/components/organisms/SendFax/SendFax.vue'))

const CreateModeratedChat = h(
  defineSidebarAsync(() => import('@/components/organisms/CreateChat/CreateChat.vue')),
  mergeProps({ mode: ConferenceType.MODERATED }),
)

const CallBackDialService = h(
  defineSidebarAsync(() => import('@/features/CallBack/web-call-backorder/web-call-backorder.vue')),
)

const navComponent = shallowRef()
const oldNavState = ref<NavState>(NavState.CHATS)
const navState = ref<NavState>(NavState.CHATS)
const isOpenSidebarMenu: Ref<boolean> = ref<boolean>(true)
const defaultTab = ref<HTMLElement | null>(null)
const mobileAsideCloseIgnoreUntil = ref(0)
const MOBILE_ASIDE_CLOSE_IGNORE_WINDOW_MS = 350

function setDefaultTab (element: Ref<HTMLElement | null>) {
  defaultTab.value = element.value
}

const closeSidebar = () => {
  if (isOpenSidebarMenu.value) {
    isOpenSidebarMenu.value = false
  }
}

const openSidebar = () => {
  if (!isOpenSidebarMenu.value) {
    isOpenSidebarMenu.value = true
  }
}

export const useSidebar = (): {
  isOpenSidebarMenu: Ref<boolean>,
  closeSidebar: () => void,
  toggleSidebarMenu: (event: MouseEvent) => void,
  shouldSkipMobileAsideClose: () => boolean,
  navComponent: ShallowRef<Component>,
  oldNavState: Ref<NavState>,
  navState: Ref<NavState>,
  setDefaultTab: (element: Ref<HTMLElement | null>) => void,
  openSidebar: () => void
} => {
  const shouldSkipMobileAsideClose = (): boolean => Date.now() <= mobileAsideCloseIgnoreUntil.value

  const toggleSidebarMenu = (event: MouseEvent): void => {
    mobileAsideCloseIgnoreUntil.value = Date.now() + MOBILE_ASIDE_CLOSE_IGNORE_WINDOW_MS
    let activeTab = event.currentTarget as HTMLElement
    // Если никакой таб не был нажат, открываем текущий таб
    if (!activeTab) {
      activeTab = event.currentTarget as HTMLElement
      isOpenSidebarMenu.value = defaultTab.value !== activeTab
      return
    }
    // Если нажали на другой таб, открываем его
    isOpenSidebarMenu.value = true
  }

  return {
    isOpenSidebarMenu,
    navComponent,
    oldNavState,
    navState,
    toggleSidebarMenu,
    shouldSkipMobileAsideClose,
    closeSidebar,
    setDefaultTab,
    openSidebar,
  }
}

watchEffect(() => {
  switch (navState.value) {
  case NavState.CHATS:
    navComponent.value = ViewChatList
    break
  case NavState.CALENDAR:
    navComponent.value = CalendarSidebar
    break
  case NavState.CALLS:
    navComponent.value = CallsWrapper
    break
  case NavState.CONTACTS:
    navComponent.value = ViewContactList
    break
  case NavState.NOTIFICATIONS:
    navComponent.value = ViewChatList
    break
  case NavState.CREATE_CHAT:
    navComponent.value = CreateChat
    break
  case NavState.CREATE_MODERATED_CHAT:
    navComponent.value = CreateModeratedChat
    break
  case NavState.SETTINGS:
    navComponent.value = defineSidebarAsync(() => import('@/widgets/sidebar').then(({ SettingsSidebar }) => SettingsSidebar))
    break
  case NavState.CALLBACK_DIAL_SERVICE:
    navComponent.value = CallBackDialService
    break
  case NavState.SEND_FAX:
    navComponent.value = SendFax
    break
  default:
    throw new Error('Unexpected nav state')
  }
})

watch(
  () => navState.value,
  (newValue: NavState, oldValue: NavState) => {
    oldNavState.value = oldValue
  },
)
